import { Fragment } from 'react';
import { Box, Button, ButtonBase, Chip, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';

import logoN from '@/assets/logo-n.png';
import { SecondBrainChat } from '../types';
import { formatRelativeTime } from '../utils/relativeTime';
import { smallTagChipSx } from './tagChipStyles';

const RECENT_CHATS_LIMIT = 5;

interface SecondBrainHomeProps {
  chatHistory: SecondBrainChat[];
  composerSlot: React.ReactNode;
  onSelectChat: (chatId: string) => void;
  onOpenPalette: () => void;
}

const getChatTitle = (chat: SecondBrainChat) =>
  chat.question ?? chat.messages.find((message) => message.role === 'user')?.content ?? 'Untitled chat';

const getChatMeta = (chat: SecondBrainChat) => {
  const noteCount = chat.contextNotes?.length ?? 0;
  const relative = formatRelativeTime(chat.createdAt);
  if (noteCount > 0 && relative) {
    return `${noteCount} note${noteCount === 1 ? '' : 's'} · ${relative}`;
  }
  return relative || `${noteCount} note${noteCount === 1 ? '' : 's'}`;
};

export const SecondBrainHome = ({ chatHistory, composerSlot, onSelectChat, onOpenPalette }: SecondBrainHomeProps) => {
  const recentChats = chatHistory.slice(0, RECENT_CHATS_LIMIT);

  return (
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 3,
        pt: { xs: 5, md: 9 },
        pb: 6,
      }}
    >
      <Stack alignItems="center" spacing={3.25} sx={{ width: '100%', maxWidth: 720 }}>
        <Box component="img" src={logoN} alt="" sx={{ height: 44, width: 'auto', opacity: 0.9 }} />
        <Stack alignItems="center" spacing={1}>
          <Typography
            component="h1"
            sx={{
              fontWeight: 300,
              fontSize: { xs: 28, sm: 34 },
              lineHeight: 1.2,
              letterSpacing: '-0.4px',
              color: 'text.primary',
              textAlign: 'center',
            }}
          >
            What do you want to know?
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Second Brain reads your notes and shows you where it found the answer.
          </Typography>
        </Stack>

        <Box sx={{ width: '100%', maxWidth: 640 }}>{composerSlot}</Box>

        {recentChats.length > 0 && (
          <Stack sx={{ width: '100%', maxWidth: 640, mt: 3.25 }} spacing={0.25}>
            <Stack direction="row" alignItems="baseline" gap={1.25} sx={{ px: 0.5, pb: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  lineHeight: '16px',
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                Pick up where you left off
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={onOpenPalette}
                sx={{ fontSize: 12, fontWeight: 500, p: 0, minWidth: 0, '&:hover': { bgcolor: 'transparent' } }}
              >
                All {chatHistory.length} chat{chatHistory.length === 1 ? '' : 's'}
              </Button>
            </Stack>
            {recentChats.map((chat, chatIndex) => (
              <Fragment key={chat.id}>
                {chatIndex > 0 && <Box sx={{ height: '1px', bgcolor: 'neutral.otherBorder', mx: 1.5 }} />}
                <ButtonBase
                  onClick={() => onSelectChat(chat.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: '11px 12px',
                    borderRadius: '8px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <ForumOutlinedIcon sx={{ fontSize: 18, color: 'grey.500', flex: 'none' }} />
                  <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
                    <Typography variant="body2" fontWeight={500} color="text.primary" noWrap>
                      {getChatTitle(chat)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {getChatMeta(chat)}
                    </Typography>
                  </Stack>
                  {chat.tags.slice(0, 2).map((tag, tagIndex) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{ ...smallTagChipSx(tagIndex), display: { xs: 'none', sm: 'inline-flex' } }}
                    />
                  ))}
                  <ChevronRightIcon sx={{ fontSize: 18, color: 'grey.400', flex: 'none' }} />
                </ButtonBase>
              </Fragment>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default SecondBrainHome;
